import {
  View,
  Text,
  StyleSheet,
  Pressable,
  ScrollView,
  ActivityIndicator,
} from "react-native";
import { useUISlice } from "@/models/useUISlice";
import ModalContainer from "@/ui/ModalContainer";
import { ms } from "@/ui/moderateScale";
import { useTranslation } from "react-i18next";
import { AUDIO_LANGUAGES, Colors, SELECT_STYLES } from "@/constants";
import PrimaryButton from "./PrimaryButton";
import { useState, useRef, useEffect } from "react";
import { useUserSlice } from "@/models/useUserSlice";
import { formatTimeToHHMMSS, getFormattedDate } from "@/models/utils";
import { useRealtimeTranscription } from "@/models/useRealtimeTranscription";
import { Note } from "@/models/types";
import { toast } from "react-hot-toast";

const AlertWidgetCreateNoteLiveTranscription = () => {
  const visible = useUISlice(
    (state) => state.alertWidget === "live_transcription"
  );
  const setVisible = useUISlice((state) => state.setAlertWidget);
  const [loading, setLoading] = useState(false);
  const width = useUISlice((state) => state.device.width);
  const isMobile = useUISlice((state) => state.device.isMobile);
  const transcriptionLanguage = useUserSlice(
    (state) => state.userData?.preferences?.transcriptionLanguage
  );

  // Add refs and state for transcript handling
  const scrollViewRef = useRef<ScrollView>(null);
  const height = useUISlice((state) => state.device.height);

  const [language, setLanguage] = useState<string>(
    transcriptionLanguage || AUDIO_LANGUAGES[0].value
  );
  const { t } = useTranslation();
  const [folderId, setFolderId] = useState<string | undefined>(undefined);
  const folders = useUserSlice((state) => {
    if (state?.folders?.length === 0) return undefined;
    return [
      { id: undefined, name: t("alertWidgetCreateNoteMediaImport.allNotes") },
      ...state.folders,
    ];
  });

  const {
    status,
    startTranscription,
    stopTranscription,
    pauseTranscription,
    resumeTranscription,
    deleteTranscription,
    transcript: transcriptObject,
  } = useRealtimeTranscription();

  const transcript: Note["transcript"] = Object.values(transcriptObject).flat();

  // Reset transcript when starting new recording
  useEffect(() => {
    if (visible) {
      deleteTranscription();
    }
  }, [visible]);

  // Add effect to scroll when formatted transcript changes
  useEffect(() => {
    if (scrollViewRef.current) {
      setTimeout(() => {
        scrollViewRef.current?.scrollToEnd({ animated: true });
      }, 100);
    }
  }, [transcript]);

  const _onDismiss = () => {
    if (status !== "inactive") {
      toast.error(t("alertWidgetCreateNoteLiveTranscription.dismissAlertText"));
      return;
    }

    setVisible(undefined);
  };

  const _onSaveTranscription = async () => {
    if (!transcript?.length || transcript.length === 0) return;

    await stopTranscription();

    useUserSlice
      .getState()
      .note()
      .create(
        {
          type: "text",
          name: `Voice recording-${getFormattedDate()}`,
          transcript,
          status: "transcription_started",
        },
        folderId
      );

    setVisible(undefined);
  };

  if (!visible) return null;

  return (
    <ModalContainer
      shouldPersistBackdropClick
      title={t("alertWidgetCreateNoteLiveTranscription.title")}
      onDismiss={_onDismiss}
      hideCancel
      modalWidth={Math.min(width, 600)}
    >
      <View style={styles.spacer} />

      <Text style={styles.label}>
        {t("alertWidgetCreateNoteLiveTranscription.audioLanguage")}
      </Text>
      <select
        style={SELECT_STYLES}
        value={language}
        onChange={(e) => setLanguage(e.target.value)}
      >
        {AUDIO_LANGUAGES.map((language) => (
          <option key={language.value} value={language.value}>
            {language.label}
          </option>
        ))}
      </select>

      {folders && (
        <>
          <View style={styles.spacer} />
          <Text style={styles.label}>
            {t("alertWidgetCreateNoteMediaImport.folder")}
          </Text>
          <select
            style={SELECT_STYLES}
            value={folderId}
            onChange={(e) => setFolderId(e.target.value)}
          >
            {folders.map((folder, index) => (
              <option key={index} value={folder.id}>
                {`📂 ${folder.name}`}
              </option>
            ))}
          </select>
        </>
      )}
      <View style={styles.spacer} />

      <ScrollView
        ref={scrollViewRef}
        style={StyleSheet.flatten([
          styles.transcriptContainer,
          { maxHeight: Math.min(height * 0.5, ms(300)) },
          isMobile && {
            minHeight: height - (folders ? ms(460) : ms(400)),
            maxHeightHeight: height - (folders ? ms(460) : ms(400)),
          },
        ])}
        contentContainerStyle={styles.transcriptContent}
      >
        {transcript.map((segment, index) => (
          <View key={index} style={styles.transcriptSegment}>
            <Text style={styles.timestamp}>
              {formatTimeToHHMMSS(segment.start)}
            </Text>
            <Text style={styles.transcriptText}>{segment.text}</Text>
          </View>
        ))}
      </ScrollView>

      <View style={styles.spacer} />

      {status === "inactive" && (
        <PrimaryButton
          loading={loading}
          iconStyle={{ tintColor: Colors.white }}
          style={{ backgroundColor: Colors.softRed }}
          icon="mic"
          text={t("alertWidgetCreateNoteLiveTranscription.startListening")}
          onPress={async () => {
            setLoading(true);
            await startTranscription(language);
            setLoading(false);
          }}
          disabled={status !== "inactive"}
        />
      )}

      {status === "transcribing" && (
        <Pressable
          onPress={pauseTranscription}
          style={{
            ...styles.button,
            minHeight: ms(60),
            backgroundColor: Colors.text,
            marginTop: ms(8),
          }}
        >
          <Text style={{ ...styles.buttonText, color: Colors.white }}>
            {t("alertWidgetCreateNoteLiveTranscription.pause")}
          </Text>
        </Pressable>
      )}

      {status === "paused" && (
        <View
          style={{
            width: "100%",
            flexDirection: "row",
            gap: ms(8),
            marginTop: ms(16),
            flex: 1,
          }}
        >
          <Pressable onPress={deleteTranscription} style={styles.button}>
            <Text style={styles.buttonText}>
              {t("alertWidgetCreateNoteLiveTranscription.delete")}
            </Text>
          </Pressable>

          <Pressable
            onPress={async () => {
              setLoading(true);
              await resumeTranscription(language);
              setLoading(false);
            }}
            style={{ ...styles.button, backgroundColor: Colors.softRed }}
          >
            {loading ? (
              <ActivityIndicator size="small" color={Colors.white} />
            ) : (
              <Text style={{ ...styles.buttonText, color: Colors.white }}>
                {t("alertWidgetCreateNoteLiveTranscription.resume")}
              </Text>
            )}
          </Pressable>

          <Pressable
            onPress={_onSaveTranscription}
            style={{ ...styles.button, backgroundColor: Colors.text }}
          >
            <Text style={{ ...styles.buttonText, color: Colors.white }}>
              {t("alertWidgetCreateNoteLiveTranscription.save")}
            </Text>
          </Pressable>
        </View>
      )}
      <View style={styles.negativeMargin} />
    </ModalContainer>
  );
};

const styles = StyleSheet.create({
  spacer: {
    height: ms(16),
  },
  label: {
    fontSize: ms(16),
    fontWeight: "400",
    color: Colors.text,
    marginBottom: ms(8),
  },
  negativeMargin: {
    marginBottom: -ms(16),
  },
  button: {
    height: ms(60),
    maxHeight: ms(60),
    flex: 1,
    backgroundColor: "#ffdedc",
    borderRadius: ms(12),
    justifyContent: "center",
    alignItems: "center",
  },
  buttonText: {
    fontWeight: "600",
    fontSize: ms(16),
    color: Colors.softRed,
  },
  transcriptContainer: {
    height: ms(300),
    backgroundColor: Colors.text + "05",
    borderRadius: ms(12),
  },
  transcriptContent: {
    padding: ms(16),
  },
  transcriptSegment: {
    marginBottom: ms(12),
  },
  timestamp: {
    fontSize: ms(11),
    color: Colors.subtitle,
    marginBottom: ms(2),
  },
  transcriptText: {
    fontSize: ms(16),
    lineHeight: ms(24),
    color: Colors.text,
    marginBottom: ms(4),
  },
});

export default AlertWidgetCreateNoteLiveTranscription;
